import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faHome } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import BreadcrumbPath from './BreadcrumbPath';

interface FolderSelectorProps {
  value: string | null;
  onChange: (folderId: string | null) => void;
  excludeFolderId?: string; // For move operations - exclude this folder and descendants
  excludePath?: string; // For move operations - exclude descendants by path
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}

interface FolderOption {
  value: string | null;
  label: string;
  path: string;
  isSpecial?: boolean;
}

/**
 * FolderSelector Component
 * Searchable folder selection dropdown with path display
 * Features:
 * - Searchable dropdown with full paths
 * - Path truncation with tooltips
 * - Folder icons
 */
const FolderSelector: React.FC<FolderSelectorProps> = ({
  value,
  onChange,
  excludeFolderId,
  excludePath,
  disabled = false,
  label = 'Destination Folder',
  placeholder = 'Search or select destination folder...'
}) => {
  const { folders } = useSelector((state: any) => state.dropbox);

  // Build folder options with full paths
  const folderOptions = useMemo(() => {
    const options: FolderOption[] = [
      { value: null, label: 'Root Folder', path: '/', isSpecial: true }
    ];

    // Filter folders
    let availableFolders = folders.filter((f: any) => {
      if (f.is_deleted) return false;
      if (excludeFolderId && f.id === excludeFolderId) return false;
      if (excludePath && f.path && f.path.startsWith(excludePath + '/')) return false;
      return true;
    });

    // Sort by path
    availableFolders.sort((a: any, b: any) => {
      const pathA = a.path || `/${a.name}`;
      const pathB = b.path || `/${b.name}`;
      return pathA.localeCompare(pathB);
    });

    availableFolders.forEach((folder: any) => {
      options.push({
        value: folder.id,
        label: folder.name,
        path: folder.path || `/${folder.name}`,
        isSpecial: false
      });
    });

    return options;
  }, [folders, excludeFolderId, excludePath]);

  // Custom option component with path display
  const formatOptionLabel = (option: FolderOption) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {option.isSpecial ? (
        <>
          <FontAwesomeIcon 
            icon={faHome} 
            style={{ marginRight: '8px', color: '#1a73e8' }} 
          />
          <span>{option.label}</span>
        </>
      ) : (
        <BreadcrumbPath path={option.path} showIcon={true} />
      )}
    </div>
  );

  // Custom filter function for searching by path
  const filterOption = (option: any, inputValue: string) => {
    const searchValue = inputValue.toLowerCase();
    const optionData = option.data as FolderOption;
    return (
      optionData.label.toLowerCase().includes(searchValue) ||
      optionData.path.toLowerCase().includes(searchValue)
    );
  };

  const selectedOption = folderOptions.find(opt => opt.value === value) || folderOptions[0];

  return (
    <Form.Group>
      {label && <Form.Label>{label}</Form.Label>}

      {/* Searchable Dropdown */}
      <Select
        value={selectedOption}
        onChange={(option) => onChange(option?.value || null)}
        options={folderOptions}
        formatOptionLabel={formatOptionLabel}
        filterOption={filterOption}
        placeholder={placeholder}
        isClearable={false}
        isSearchable={true}
        isDisabled={disabled}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '38px',
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
      />
      
      <Form.Text className="text-muted">
        Type to search by folder name or path
      </Form.Text>
    </Form.Group>
  );
};

export default FolderSelector;
